import { acceptsTypedValue } from 'utils/lookupHelper';
import { formControlActions } from 'store/formControlSlice/formControlSlice';
import { store } from 'store/store';
import { cleanInputValue, formatInputValue, validateInputValue } from './fieldValdiation/utils';
import {
    AUTOMATION_SOURCE,
    QUICK_FILL_EXCLUDED_FIELDS,
    PROCESS_TIME_FIELD,
    DRIVER_NAME_FIELD,
    PROCESS_TIME_DEFAULT_SUGGESTION,
    isUnknownSentinel,
} from 'utils/constants';
import { driverResolvesToSlash, shouldSlashOverrideProcessTime } from 'utils/processTime';
import { mergeProtocolAutomation, selectProtocolAutomationByTransactionId } from 'features/stepByStepProtocol';

let fieldChanged = false;

export const isQuickFillExcludedField = (field) => QUICK_FILL_EXCLUDED_FIELDS.includes(field?.dataField);

export const changeFieldValue = function (formId, fieldName, value = null, error) {
    store.dispatch(
        formControlActions.setFieldValue({
            formId,
            fieldName,
            value,
        })
    );

    if (error) {
        store.dispatch(
            formControlActions.setFieldError({
                formId,
                fieldName,
                error: false,
                helperText: '',
            })
        );
    }
};

const changeFieldValueAndMarkEdited = function (formId, fieldName, value = null, error) {
    store.dispatch(
        formControlActions.setFieldValueAndMarkEdited({
            formId,
            fieldName,
            value,
        })
    );

    if (error) {
        store.dispatch(
            formControlActions.setFieldError({
                formId,
                fieldName,
                error: false,
                helperText: '',
            })
        );
    }
};

export const changeHandler = function (e, newValue) {
    const { lookup, formId, fieldName, error, type, referenceField, autofillFormFields } = this;
    fieldChanged = true;

    let value =
        newValue !== undefined ? (lookup.displayExpr ? (lookup.valueExpr ? newValue?.[lookup.valueExpr] : newValue) : newValue) : e.target.value;

    if (type === 'number' && value !== '') value = +value;

    changeFieldValueAndMarkEdited(formId, fieldName, value, error);
    autofillFormFields?.(newValue);

    if (referenceField) changeFieldValue(formId, referenceField, newValue[lookup.referenceExpr]);
};

/**
 * Commits what an Agent types, for every field that accepts a value outside its options.
 *
 * The condition used to be `!lookup?.displayExpr || referenceField`, which left out a list the
 * location opened with `restrictToOptions: false`: the control rendered free-solo and showed the
 * typed text, but nothing wrote it to the form, so the field stayed empty and CTRL+Enter filled it
 * with '/'. A value typed on a closed list is still ignored here - the options are the only
 * permitted values, and the control does not offer free text there either.
 *
 * Text that exactly matches an option's label is upgraded to that option's value on blur
 * (`AIQAutocomplete.inputBlurHandler` resolves it through the lookup), so storing the raw text while
 * typing cannot leave a label where a value belongs.
 */
export const inputValueChangeHandler = function (_, newValue) {
    const { lookup, formId, fieldName, error, type, referenceField, referenceValue, restrictToOptions } = this;
    fieldChanged = true;
    if (acceptsTypedValue({ lookup, referenceField, restrictToOptions })) {
        const value = type === 'number' ? +newValue : newValue;
        changeFieldValueAndMarkEdited(formId, fieldName, value, error);
        if (referenceValue && referenceField !== fieldName) changeFieldValue(formId, referenceField, null);
    }
};

export const blurHandler = function blurHandler(e) {
    const { required, touched, error, formId, fieldName, validationField, validationRules, format, disableInventoryValidation } = this;
    let value = e.target.value;
    if (fieldName.includes('[]')) {
        value = value.split(',').map((value) => formatInputValue(cleanInputValue(value), format));
    } else {
        value = formatInputValue(cleanInputValue(value), format);
    }
    value !== e.target.value && changeFieldValue(formId, fieldName, value, error);

    const errorMessage = validateInputValue(value, validationRules);
    const hasError = value === '' ? required && !error : errorMessage;

    if (hasError) {
        store.dispatch(
            formControlActions.setFieldError({
                formId,
                fieldName,
                error: hasError,
            })
        );
    }

    if (!touched) {
        store.dispatch(
            formControlActions.setFieldTouched({
                formId,
                fieldName,
            })
        );
    }
    if (!disableInventoryValidation)
        store.dispatch(
            formControlActions.validateTransactionWithInventoryData({
                formId,
            })
        );

    if (validationField && fieldChanged && !e.target.inputBlurOnQuickSubmit) {
        store.dispatch(
            formControlActions.validateTransactionFormField({
                formId,
                fieldName,
            })
        );
    }
    fieldChanged = false;
    if (e.target.inputBlurOnSubmit) e.target.inputBlurOnQuickSubmit = false;
};

export const getFieldConfig = function ({
    formId,
    fieldName,
    label,
    required,
    type,
    helperText = 'This field is required',
    value,
    error,
    touched,
    initialValue,
    disabled,
    inputProps,
}) {
    const customLabel = required ? label + '*' : label;
    const customHelperText = typeof error === 'string' ? error : helperText;
    return {
        formId,
        fieldName,
        label: customLabel,
        required,
        type,
        changeHandler,
        blurHandler,
        inputValueChangeHandler,
        helperText: customHelperText,
        value,
        error,
        touched,
        initialValue,
        disabled,
        inputProps,
        changeFieldValue,
    };
};

export const displayFieldCustomError = function (formId, fieldName, errorText) {
    store.dispatch(
        formControlActions.setFieldError({
            formId,
            fieldName,
            error: errorText,
        })
    );

    store.dispatch(
        formControlActions.setFieldTouched({
            formId,
            fieldName,
        })
    );
};

export function populateEmptyTransactionFormFields(formObj, defaultValue = '/') {
    const {
        form: { values, errors, automation: formAutomation },
        formConfig: { fields },
        transactionData: { id: formId },
    } = formObj;
    // Step-by-step protocol `/` suggestions take part with the lowest priority (never stored in the form).
    const automation = mergeProtocolAutomation(formAutomation, selectProtocolAutomationByTransactionId(store.getState(), formId));

    applyProcessTimeSlashOverrideOnQuickSubmit(formObj, defaultValue);

    const fieldsToPopulate = getEmptyFormFields(values);

    if (!fieldsToPopulate.length) return store.getState().forms.forms[formId];

    fields.forEach((field) => {
        const { dataField: fieldName, validationRules, format } = field;
        const isRequired = !!(validationRules && validationRules.find(({ type }) => type === 'required'));
        if (isRequired && fieldsToPopulate.includes(fieldName)) {
            if (isQuickFillExcludedField(field)) {
                store.dispatch(
                    formControlActions.setFieldError({
                        formId,
                        fieldName,
                        error: true,
                        helperText: 'Please select a value',
                    })
                );
                store.dispatch(
                    formControlActions.setFieldTouched({
                        formId,
                        fieldName,
                    })
                );
                return;
            }

            const value = resolveQuickSubmitFillValue(fieldName, values, automation, defaultValue);
            const source = resolveQuickSubmitFillSource(fieldName, value, automation);

            let formatedValue;
            if (fieldName.endsWith('[]')) {
                formatedValue = value.split(',').map((value) => formatInputValue(cleanInputValue(value), format));
            } else {
                formatedValue = formatInputValue(cleanInputValue(value), format);
            }
            populateTransactionFormField(formId, errors, field, formatedValue, source);
            const error = validateInputValue(formatedValue, validationRules);
            if (error) {
                store.dispatch(
                    formControlActions.setFieldError({
                        formId,
                        fieldName,
                        error,
                    })
                );
            }
        }
    });
    const newFormObj = store.getState().forms.forms[formId];
    return newFormObj;
}

export function resolveQuickSubmitFillValue(fieldName, values, automation, defaultValue) {
    if (fieldName === PROCESS_TIME_FIELD) {
        const driverValue = values?.[DRIVER_NAME_FIELD];
        const driverSuggestionValue = automation?.[DRIVER_NAME_FIELD]?.sourceValue;
        if (driverResolvesToSlash({ driverValue, driverSuggestionValue })) {
            return defaultValue;
        }
        return automation?.[fieldName]?.sourceValue ?? PROCESS_TIME_DEFAULT_SUGGESTION;
    }
    const rawSourceValue = automation?.[fieldName]?.sourceValue;
    return isFillableSuggestion(rawSourceValue) ? rawSourceValue : defaultValue;
}

/**
 * Whether a suggestion's `sourceValue` can stand in for an empty field on submit: not null, not the
 * unknown sentinel, and not blank. alisa sends `value: ''` when it cannot read a plate, and an
 * inventory item leaves `sourceValue: ''` for a field it has no value for.
 */
const isFillableSuggestion = (sourceValue) => sourceValue != null && !isUnknownSentinel(sourceValue) && cleanInputValue(sourceValue) !== '';

/**
 * The automation source of a quick-submit fill, or `null` when the field was not filled from a real
 * suggestion (so activity tracking reports the true automation_source instead of `unknown`).
 *
 * Only a genuine suggestion counts: the fill used the suggestion's own `sourceValue` (not the `'/'`
 * default), the suggestion carries a `source`, and the value is not blank or the unknown sentinel.
 */
export function resolveQuickSubmitFillSource(fieldName, filledValue, automation) {
    const suggestion = automation?.[fieldName];
    const rawSourceValue = suggestion?.sourceValue;
    if (!suggestion?.source || !isFillableSuggestion(rawSourceValue)) return null;
    // AUTO-sourced fields (e.g. Process Time) are reported by their own auto-suggestion seam; do not
    // also stamp them here, which would emit a second value.applied through the direct seam.
    if (suggestion.source === AUTOMATION_SOURCE.AUTO) return null;
    return filledValue === rawSourceValue ? suggestion.source : null;
}

function applyProcessTimeSlashOverrideOnQuickSubmit(formObj, defaultValue) {
    const {
        form: { values, errors, automation, fieldValueSources, userEdited },
        formConfig: { fields },
        transactionData: { id: formId },
    } = formObj;

    const processTimeField = fields.find((field) => field.dataField === PROCESS_TIME_FIELD);

    const shouldOverride = shouldSlashOverrideProcessTime({
        hasField: !!processTimeField,
        processTimeValue: values?.[PROCESS_TIME_FIELD],
        processTimeSource: fieldValueSources?.[PROCESS_TIME_FIELD],
        processTimeUserEdited: userEdited?.[PROCESS_TIME_FIELD],
        driverValue: values?.[DRIVER_NAME_FIELD],
        driverSuggestionValue: automation?.[DRIVER_NAME_FIELD]?.sourceValue,
    });
    if (!shouldOverride) return;

    populateTransactionFormField(formId, errors, processTimeField, defaultValue);
}

const getEmptyFormFields = (valuesObj) =>
    Object.keys(valuesObj).reduce((emptyFields, fieldName) => {
        const currentValue = valuesObj[fieldName];
        if (!currentValue && currentValue !== false && currentValue !== 0) emptyFields.push(fieldName);
        return emptyFields;
    }, []);

/**
 * Where CTRL+Enter's fill value has to land on a given field.
 *
 * A dropdown stores an option's value while the fill carries its display text ('/'), so the text is
 * resolved through the lookup first. Matching nothing leaves the field empty, which is right for a
 * closed list - the location declared those the only permitted values, and quick submit must not
 * invent one - but wrong for a list the location opened with `restrictToOptions: false`, where an
 * Agent may type anything: whatever they can type by hand, quick submit must be able to fill. The
 * condition mirrors the freeSolo rule in `Select`.
 *
 * @returns {{type: 'raw'}|{type: 'option', option: object}|{type: 'none'}}
 */
export const resolveQuickSubmitFillTarget = (field, newValue) => {
    const lookup = field?.lookup;

    //standard inputs or dropdowns with free solo
    if (!lookup || !lookup.displayExpr || field.referenceField) return { type: 'raw' };

    const option = (lookup.dataSource || []).find((item) => item[lookup.displayExpr] === newValue);
    if (option) return { type: 'option', option };

    return acceptsTypedValue(field) ? { type: 'raw' } : { type: 'none' };
};

const populateTransactionFormField = (formId, errors, field, newValue, source = null) => {
    const { dataField, lookup, referenceField, dataType } = field;
    const hasError = errors?.[dataField];
    const target = resolveQuickSubmitFillTarget(field, newValue);

    if (target.type === 'raw') {
        changeFieldValue(formId, dataField, newValue, hasError);
        // Pair the value with its automation source (like the copy handlers), so the direct
        // value-applied seam reports the real automation_source instead of `unknown`.
        if (source) {
            store.dispatch(formControlActions.setFieldValueSource({ formId, fieldName: dataField, source }));
        }
        return;
    }

    if (target.type === 'option') {
        // A quick-submit suggestion fill is recorded as an automation value (value + source) rather
        // than a user edit, so activity tracking reports the real automation_source. Mirrors
        // changeHandler's value resolution, minus the user-edited mark. Strict dropdowns here never
        // carry a referenceField - those route to the free-solo branch above.
        if (source) {
            let resolvedValue = lookup.valueExpr ? target.option[lookup.valueExpr] : target.option;
            if (dataType === 'number' && resolvedValue !== '') resolvedValue = +resolvedValue;
            changeFieldValue(formId, dataField, resolvedValue, hasError);
            store.dispatch(formControlActions.setFieldValueSource({ formId, fieldName: dataField, source }));
            return;
        }
        changeHandler.call({ lookup, formId, fieldName: dataField, error: hasError, referenceField, type: dataType }, undefined, target.option);
    }
};
